import '@features/compare/components/compare-button/compare-button';

import { defineElement, FC, useBindMethod, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { ButtonRoundA } from '@atoms/button-round/button-round';
import { Image } from '@atoms/image/image';
import {
    COLOR_CLASSES,
    FormattedPrice,
    FormattedSpecification,
    FormattedStatus,
} from '@source/utilities/api-formatters';
import { useStylesheet } from '@utilities/hooks';

import style from './construction-number-card.scss';

const HIGHLIGHTED_CLASS = 'construction-number-card--highlighted';

export interface ConstructionNumberCardElement extends HTMLElement {
    setHighlighted: () => void;
    unsetHighlighted: () => void;
}

const ConstructionNumberCard: FC<ConstructionNumberCardElement> = ({ element }) => {
    const [id] = useProp<string>('id', '');
    const [houseName] = useProp<string>('houseName', '');
    const [wishlistItemCount] = useProp<number>('wishlistItemCount', 0);
    const [href] = useProp<string>('href', '');
    const [price] = useProp<FormattedPrice | false>('price', false);
    const [image] = useProp<{ alt: string; url: string }>('image');
    const [specs] = useProp<Array<FormattedSpecification>>('specs', []);
    const [shoppingCartUrl] = useProp<string | undefined | false>('shoppingCartUrl', false);
    const [classes] = useProp<string | null>('classes', null);
    const [status] = useProp<FormattedStatus | null>('status', null);
    const [compare] = useProp<boolean>('compare', false);
    const [hidePrice] = useProp<boolean>('hidePrice', false);
    const [isNhg] = useProp<boolean>('isNhg', false);
    const [debugId] = useProp<string | null>('debugId', null);

    useStylesheet(style);

    function setHighlighted() {
        element.classList.add(HIGHLIGHTED_CLASS);
    }

    function unsetHighlighted() {
        element.classList.remove(HIGHLIGHTED_CLASS);
    }

    useBindMethod('setHighlighted', setHighlighted);
    useBindMethod('unsetHighlighted', unsetHighlighted);

    let className = 'c-construction-number-card';
    className += classes ? ` ${classes}` : '';

    return (
        <a className={className} href={href} id={id}>
            <div class="construction-number-card__image-container">
                <Image
                    classes="construction-number-card__image"
                    alt={image.alt}
                    image={image.url}
                    settings={{
                        '640w': {
                            width: 232,
                            height: 144,
                        },
                    }}
                    objectFit={true}
                />
            </div>

            <div class="construction-number-card__details">
                <div>
                    {status && (
                        <span
                            class={`construction-number-card__status ${
                                COLOR_CLASSES[status.color]
                            }`}>
                            {status.label}
                        </span>
                    )}
                    <h3 class="construction-number-card__title">{houseName}</h3>
                    {price && price.amount && !hidePrice && (
                        <span class="construction-number-card__price">
                            {price.prefix && price.prefix} {price.amount && price.amount}{' '}
                            {price.suffix && price.suffix}
                            {isNhg && (
                                <span class="u-margin-left-1 u-color--positive u-label">NHG</span>
                            )}
                        </span>
                    )}
                    <ul class="construction-number-card__specs-list">
                        {specs.map((spec) => (
                            <li class="construction-number-card__list-item">
                                {spec.value} {spec.suffix}
                            </li>
                        ))}
                    </ul>
                </div>
                {debugId && <span>{debugId}</span>}
                {compare && <compare-button class="construction-number-card__compare" id={id} />}
            </div>

            <div class="construction-number-card__actions">
                {window.__ENVIRONMENT_DETAILS__.hasWishlist && (
                    <div class="construction-number-card__action">
                        <bpd-wishlist-favourite-button
                            construction-number-id={id}
                            count={wishlistItemCount}
                        />
                    </div>
                )}
                {shoppingCartUrl && (
                    <div class="construction-number-card__action">
                        <ButtonRoundA
                            ariaLabel="Voeg toe aan winkelwagen"
                            size="small"
                            classes="construction-number-card__add-to-cart"
                            svg="icons-cart"
                            url={shoppingCartUrl}
                        />
                    </div>
                )}
            </div>
        </a>
    );
};

ConstructionNumberCard.props = {
    id: { type: String },
    houseName: { type: String },
    wishlistItemCount: { type: Number },
    href: { type: String },
    price: { type: Object },
    image: { type: Object, required: true },
    specs: { type: Array },
    shoppingCartUrl: { type: String },
    classes: { type: String },
    status: { type: Object },
    compare: { type: Boolean },
    hidePrice: { type: Boolean },
    isNhg: { type: Boolean },
    debugId: { type: String },
};

defineElement('bpd-construction-number-card', ConstructionNumberCard);
